<template>
  <div class="toolStatements">
    <draggable v-model="statements"
               handle=".handle"
               @update="onUpdated">
      <transition-group>
        <div v-for="item in statements"
             :key="item.statementId"
             class="toolStatements__container">
          <div class="toolStatements__background"></div>
          <div
            class="handle toolStatements__handle"
            data-toggle="tooltip"
            data-placement="right"
            title="Hold click and drag to reorder">
            <font-awesome-icon :icon="['fas', 'caret-up']"></font-awesome-icon>
            <font-awesome-icon :icon="['fas', 'caret-down']"></font-awesome-icon>
          </div>
          <div class="toolStatements__statement">
            {{ item.statement }}
          </div>
        </div>
      </transition-group>
    </draggable>
    <prev-next @previous="interceptorPrevious(prev)"
               @next="interceptorNext(next)">
    </prev-next>
  </div>
</template>
<script>
import Draggable from "vuedraggable";
import { INSTANCE_TYPE } from "~/Components/constants";
import {
  listQuestions as listTeamStatements,
  saveAnswers as saveTeamStatementOrder,
} from "~/Scripts/resources/teamResource";
import { utils, backInterceptor } from "~/Scripts/vue/mixins/common";
import PrevNext from "~/Components/Common/PrevNext.vue";

export default {
  name: "ToolStatement",
  components: {
    Draggable,
    PrevNext,
  },
  mixins: [utils, backInterceptor],
  props: {
    instanceStatements: {
      type: Array,
      default: () => null,
    },
    memberId: {
      type: String,
      default: null,
    },
    instanceId: {
      type: String,
      required: true,
    },
    instanceType: {
      type: String,
      required: true,
      validator: val => [INSTANCE_TYPE.TEAM].includes( val.toUpperCase() ),
    },
    saveOnUpdate: {
      type: Boolean,
      default: false,
    },
    saveOnLoad: {
      type: Boolean,
      default: false,
    },
    saveOnNext: {
      type: Boolean,
      default: true,
    },
    next: {
      type: String,
      required: true,
    },
    prev: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      instance: {
        id: null,
      },
      statements: [],
      mutableChosenStatementOrder: [],
    };
  },
  computed: {
    loadStatements () {
      switch ( this.instanceType ) {
        case INSTANCE_TYPE.TEAM:
          return this.loadTeamStatements;
        default:
          throw new Error( "Load statements not defined." );
      }
    },
    saveStatements () {
      switch ( this.instanceType ) {
        case INSTANCE_TYPE.TEAM:
          return this.saveTeamStatements;
        default:
          throw new Error( "Save statements not defined " );
      }
    },
  },
  async created () {
    const params = this.getURLParams();
    this.instance.id = this.instanceId || params.id;

    this.statements = await this.loadStatements();
    this.defaultStatementOrder();

    if ( this.saveOnLoad ) {
      this.saveStatements();
    }
    if ( this.saveOnNext ) {
      this.setAsyncInterceptorSaveMethod( this.saveStatements );
    }
  },
  methods: {
    defaultStatementOrder () {
      this.mutableChosenStatementOrder = this.statements.map( s => s.statementId );
    },
    loadTeamStatements () {
      const error = new Error( "Failed to retrieve statements" );
      return new Promise( ( resolveLoad, reject ) => {
        if ( Array.isArray( this.statements ) && this.statements.length > 0 ) {
          resolveLoad( this.statements );
        }

        try {
          listTeamStatements().then( ( { status, data } ) => {
            if ( status === 200 && data ) {
              resolveLoad( data );
            } else {
              reject( error );
            }
          } );
        } catch ( err ) {
          console.error( err );
          reject( err );
        }
      } );
    },
    saveTeamStatements () {
      const error = new Error( "Failed to save statement order" );

      const { id: instanceId } = this.instance;

      const payload = {
        instanceId,
        statementRank: this.mutableChosenStatementOrder,
        memberId: this.memberId ? this.memberId : JSON.parse( this.memberId ),
        areasOfStrength: JSON.parse( null ),
        areasOfWeakness: JSON.parse( null ),
      };

      return new Promise( ( resolveSave, reject ) => {
        try {
          saveTeamStatementOrder( payload ).then( ( { status, data } ) => {
            if ( status === 200 && data ) {
              resolveSave( data );
            } else {
              reject( error );
            }
          } );
        } catch ( err ) {
          console.error( err );
          reject( err );
        }
      } );
    },
    onUpdated () {
      this.mutableChosenStatementOrder = this.statements.map( s => s.statementId );
      if ( this.saveOnUpdate ) {
        this.saveAnswers();
      }
    },
  },
};
</script>
<style scoped lang="less">
@import "../../../Styles/utils/index.less";
@border-radius: 10px;

.toolStatements {
  &__container {
    position: relative;
    margin: 0 0 16px;
    display: flex;
    border: 1px solid @blue;
    .border-radius(@border-radius);
    overflow: hidden;
  }

  &__statement {
    padding: 12px 24px;
  }

  &__background {
    .opacity-background(@light-blue, 0.7);
    .border-radius(@border-radius);
  }

  &__handle {
    display: flex;
    flex-flow: column;
    justify-content: space-between;
    background: @blue;
    padding: 12px;
    color: #fff;
  }
}
</style>
